// dependencies
import React from "react";

// icons
import { faMemoCircleCheck } from "@performyard/fontawesome/pro-regular-svg-icons";
// components
import { Chip, ICellRendererParams, Icon, Link, PYColumnDef, Text, Tooltip } from "@performyard/py-components";

// types
import type { ReviewCycleListItem } from "../types";

const CYCLE_TYPES: Record<ReviewCycleListItem["type"], { label: string; color: "primary" | "green" | "yellow" }> = {
	manual: { label: "Manual", color: "primary" },
	automated: { label: "Automated", color: "green" },
	group: { label: "Group", color: "yellow" },
};

const NameCellRenderer = (params: ICellRendererParams<ReviewCycleListItem>) => {
	const reviewCycle = params.data;
	if (!reviewCycle) return null;

	const { name, _id, easyAddConfigCount } = reviewCycle;
	const hasEasyAdd = (easyAddConfigCount ?? 0) > 0;

	return (
		<div className="flex h-full items-center gap-2">
			<Link href={`/review-cycle/${_id}/`} underline="hover">
				<Text size="paragraph-3" dangerouslySetClassName="py-line-clamp-1">
					{name}
				</Text>
			</Link>
			{/* No asChild: Icon drops the trigger's hover handlers, so the tooltip never opens. Flexing the
			    button it renders instead removes the descender space its inline icon layout reserves. */}
			{hasEasyAdd && (
				<span
					data-testid="easy-add-indicator"
					className="flex shrink-0 items-center [&_button]:flex [&_button]:items-center"
				>
					<Tooltip content="360 Review Enabled">
						<Icon icon={faMemoCircleCheck} size={16} />
						<span className="sr-only">360 Review Enabled</span>
					</Tooltip>
				</span>
			)}
		</div>
	);
};

const CycleTypeCellRenderer = (params: ICellRendererParams<ReviewCycleListItem>) => {
	const reviewCycle = params.data;
	if (!reviewCycle) return null;

	const cycleType = CYCLE_TYPES[reviewCycle.type];
	if (!cycleType) return null;

	return (
		<div className="flex h-full items-center">
			<Chip color={cycleType.color} label={cycleType.label} size={24} />
		</div>
	);
};

const StatusCellRenderer = (params: ICellRendererParams<ReviewCycleListItem>) => {
	const reviewCycle = params.data;
	if (!reviewCycle) return null;

	const { status, progress } = reviewCycle;
	// Closed cycles are surfaced as Completed.
	const displayStatus = status === "closed" ? "completed" : status;
	const statusLabel = displayStatus.charAt(0).toUpperCase() + displayStatus.slice(1);
	const statusText = `${statusLabel} (${progress.completedReviews}/${progress.totalReviews})`;

	return (
		<div className="flex h-full items-center">
			<Text size="paragraph-3" dangerouslySetClassName="py-line-clamp-1">
				{statusText}
			</Text>
		</div>
	);
};

const getReviewCycleTableColsRefresh = (): PYColumnDef<ReviewCycleListItem>[] => {
	return [
		{
			field: "name",
			headerName: "Review Cycle Name",
			sortable: true,
			resizable: false,
			width: 416,
			cellRenderer: NameCellRenderer,
			// The grid re-sorts the fetched page client-side, and its default string compare is raw
			// UTF-16 order, which blocks every capitalised name above every lower-case one. Match the
			// endpoint's collation so the two agree.
			comparator: (a: string, b: string) => (a ?? "").localeCompare(b ?? "", "en", { caseFirst: "upper" }),
		},
		{
			field: "type",
			headerName: "Cycle Type",
			sortable: true,
			resizable: false,
			width: 160,
			cellRenderer: CycleTypeCellRenderer,
		},
		{
			field: "createdAt",
			headerName: "Created Date",
			sortable: true,
			resizable: false,
			width: 160,
			type: "date",
			cellRendererParams: {
				dateFormat: "MMM dd, yyyy",
			},
		},
		{
			field: "startDate",
			headerName: "Start Date",
			sortable: true,
			resizable: false,
			width: 190,
			type: "date",
			cellRendererParams: {
				dateFormat: "MMM dd, yyyy h:mmaaa",
			},
		},
		{
			field: "status",
			headerName: "Status",
			sortable: true,
			resizable: false,
			width: 170,
			cellRenderer: StatusCellRenderer,
		},
	];
};

export default getReviewCycleTableColsRefresh;
